// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-18) — countries by their outline, for Analyze's
 * region picker (docs/AYNI_XR_WHEN_EXTREMES_PLAN.md, step 3).
 *
 * Until now a "country" in Analyze was a rough box (`regions.ts`): Spain's
 * took in Portugal and a stretch of sea. These are Natural Earth's 1:50m
 * outlines (public domain; the de facto boundaries its default view draws,
 * as the globe's own borders are), built into
 * `public/regions/countries-ne50m-v1.json` by
 * `scripts/regions/build-countries.ts` and loaded once, when Analyze opens.
 *
 * A country becomes cells of the dataset's own grid: a cell belongs to it
 * when the cell's centre falls inside the outline (even-odd over every ring,
 * so lakes and enclaves such as Lesotho are left out of South Africa). The
 * statistics, the area over a threshold and the region through time then
 * read only those cells (`datasetStats.TexelWindow.mask`). A country smaller
 * than one cell (Monaco on a 25 km grid) gets the one cell holding it, and
 * the panel says so.
 *
 * Names: the browser's own names for the ISO code in the reader's language
 * (`Intl.DisplayNames`), else Natural Earth's English or Spanish.
 */

import { latLonToTexelUv, texelUvToLatLon } from '../services/datasetProbe'
import { windowForBounds, type LatLonBounds, type TexelWindow } from '../services/datasetStats'
import type { LumaSnapshot } from '../services/glLumaSampler'
import type { DatasetOverlayOptions } from '../types'

/** Where the file is served, and what it is (the CSV and the methods name it). */
export const COUNTRIES_URL = '/regions/countries-ne50m-v1.json'
export const COUNTRIES_SOURCE = 'Natural Earth 1:50m Admin 0 - Countries v5.1.2 (public domain; de facto boundaries), cells whose centres fall inside the outline'

export interface Country {
  /** Natural Earth's ADM0_A3: the picker's value. */
  readonly id: string
  /** ISO 3166-1 alpha-2, when it names this outline alone. */
  readonly a2?: string
  readonly en: string
  readonly es: string
  readonly bounds: LatLonBounds
  /** Rings as [lon, lat] pairs, flat: [lon0, lat0, lon1, lat1, …]. */
  readonly rings: readonly Float64Array[]
}

interface CountriesFile {
  readonly format: string
  readonly version: number
  readonly quantumDeg: number
  readonly countries: ReadonlyArray<{ id: string; a2?: string; en: string; es: string; bbox: [number, number, number, number]; rings: number[][] }>
}

/** Decode the file's rings (differences in quanta) to degrees. */
export function decodeCountries(file: CountriesFile): Country[] {
  if (file.format !== 'ayni-countries' || file.version !== 1) throw new Error('countries: unknown file format')
  const q = file.quantumDeg
  return file.countries.map((c) => ({
    id: c.id,
    ...(c.a2 ? { a2: c.a2 } : {}),
    en: c.en,
    es: c.es,
    bounds: { w: c.bbox[0], s: c.bbox[1], e: c.bbox[2], n: c.bbox[3] },
    rings: c.rings.map((enc) => {
      const out = new Float64Array(enc.length)
      let lon = 0
      let lat = 0
      for (let i = 0; i < enc.length; i += 2) {
        lon = i === 0 ? enc[0] : lon + enc[i]
        lat = i === 0 ? enc[1] : lat + enc[i + 1]
        out[i] = lon * q
        out[i + 1] = lat * q
      }
      return out
    }),
  }))
}

let loaded: Country[] | null = null
let loading: Promise<Country[] | null> | null = null
const waiting = new Set<() => void>()

/** The countries, once loaded; null before (call {@link loadCountries}). */
export function countries(): readonly Country[] | null {
  return loaded
}

/** Load the outlines once; resolves null when they cannot be had (offline, a test). */
export function loadCountries(fetcher: typeof fetch | undefined = typeof fetch === 'function' ? fetch : undefined): Promise<Country[] | null> {
  if (loaded) return Promise.resolve(loaded)
  if (loading) return loading
  if (!fetcher) return Promise.resolve(null)
  loading = fetcher(COUNTRIES_URL)
    .then((res) => (res.ok ? res.json() : null))
    .then((file: CountriesFile | null) => {
      loaded = file ? decodeCountries(file) : null
      if (!loaded) loading = null
      for (const fn of [...waiting]) fn()
      waiting.clear()
      return loaded
    })
    .catch(() => {
      loading = null
      return null
    })
  return loading
}

/** Call back once the outlines arrive (now, when they already have). */
export function onCountriesLoaded(fn: () => void): void {
  if (loaded) fn()
  else waiting.add(fn)
}

/** For tests: set the list as if loaded (null forgets it). */
export function setCountriesForTest(list: Country[] | null): void {
  loaded = list
  loading = null
  maskCache.clear()
}

export function countryById(id: string): Country | null {
  return loaded?.find((c) => c.id === id) ?? null
}

const displayNames = new Map<string, Intl.DisplayNames | null>()

/** The country's name in a language: the browser's for its ISO code, else Natural Earth's. */
export function countryName(c: Country, lang: string): string {
  if (c.a2) {
    let dn = displayNames.get(lang)
    if (dn === undefined) {
      try {
        dn = new Intl.DisplayNames([lang], { type: 'region' })
      } catch {
        dn = null
      }
      displayNames.set(lang, dn)
    }
    const name = dn?.of(c.a2)
    if (name && name !== c.a2) return name
  }
  return lang === 'en' ? c.en : c.es || c.en
}

// ---------------------------------------------------------------- the cells

/** A country on a grid: the window around it, the mask of its cells, and whether it was smaller than one cell. */
export interface CountryCells {
  readonly window: TexelWindow
  readonly cells: number
  /** True when no cell centre fell inside, and the one cell holding the country stands for it. */
  readonly tiny: boolean
}

const maskCache = new Map<string, CountryCells | null>()

/**
 * The country's cells on this frame's grid; null when it lies outside the
 * dataset (a regional box elsewhere). Cached by country and grid.
 */
export function countryCells(country: Country, snapshot: Pick<LumaSnapshot, 'width' | 'height'>, options?: DatasetOverlayOptions): CountryCells | null {
  const key = `${country.id}|${snapshot.width}x${snapshot.height}|${JSON.stringify(options?.boundingBox ?? null)}|${options?.isFlippedInY ?? false}|${JSON.stringify(options?.cropRect ?? null)}`
  if (maskCache.has(key)) return maskCache.get(key)!
  const out = rasterize(country, snapshot, options)
  if (maskCache.size > 64) maskCache.clear()
  maskCache.set(key, out)
  return out
}

function rasterize(country: Country, snapshot: Pick<LumaSnapshot, 'width' | 'height'>, options?: DatasetOverlayOptions): CountryCells | null {
  const { width, height } = snapshot
  const box = windowForBounds({ data: new Uint8Array(0), width, height }, country.bounds, options)
  // No cell centre in its box: smaller than a cell (or outside the dataset).
  if (!box) return oneCell(country, width, height, options)
  const w = box.x1 - box.x0
  const h = box.y1 - box.y0
  const mask = new Uint8Array(w * h)
  // Each column's centre longitude, once.
  const colLon = new Float64Array(w)
  for (let x = 0; x < w; x++) colLon[x] = texelUvToLatLon({ u: (box.x0 + x + 0.5) / width, v: 0.5 }, options).lon
  const crossings: number[] = []
  let cells = 0
  for (let y = 0; y < h; y++) {
    const lat = texelUvToLatLon({ u: 0.5, v: (box.y0 + y + 0.5) / height }, options).lat
    crossings.length = 0
    for (const ring of country.rings) {
      const n = ring.length / 2
      for (let i = 0, j = n - 1; i < n; j = i++) {
        const latI = ring[i * 2 + 1]
        const latJ = ring[j * 2 + 1]
        // Half-open, so a vertex on the row is counted once.
        if ((latI > lat) === (latJ > lat)) continue
        const lonI = ring[i * 2]
        const lonJ = ring[j * 2]
        crossings.push(lonI + ((lat - latI) / (latJ - latI)) * (lonJ - lonI))
      }
    }
    if (crossings.length < 2) continue
    crossings.sort((a, b) => a - b)
    for (let x = 0; x < w; x++) {
      // Even-odd: inside when an odd number of crossings lie west of the centre.
      const lon = colLon[x]
      let lo = 0
      let hi = crossings.length
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (crossings[mid] < lon) lo = mid + 1
        else hi = mid
      }
      if (lo & 1) {
        mask[y * w + x] = 1
        cells++
      }
    }
  }
  if (cells > 0) return { window: { ...box, mask }, cells, tiny: false }
  return oneCell(country, width, height, options)
}

/** Smaller than a cell: the cell holding the outline's first point stands for it; null outside the dataset. */
function oneCell(country: Country, width: number, height: number, options?: DatasetOverlayOptions): CountryCells | null {
  const first = country.rings[0]
  const uv = first ? latLonToTexelUv(first[1], first[0], options) : null
  if (!uv) return null
  const x = Math.min(width - 1, Math.max(0, Math.floor(uv.u * width)))
  const y = Math.min(height - 1, Math.max(0, Math.floor(uv.v * height)))
  return { window: { x0: x, y0: y, x1: x + 1, y1: y + 1, mask: Uint8Array.of(1) }, cells: 1, tiny: true }
}

/** The outline's rings as [lat, lon] points, for drawing it on the globe. */
export function countryOutline(country: Country): { lat: number; lon: number }[][] {
  return country.rings.map((ring) => {
    const pts: { lat: number; lon: number }[] = []
    for (let i = 0; i < ring.length; i += 2) pts.push({ lon: ring[i], lat: ring[i + 1] })
    if (pts.length) pts.push({ ...pts[0] })
    return pts
  })
}
